import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ThemeContext } from '../../../context/theme-context';
import * as ReactDOMServer from "react-dom/server";
import am5locales_tr_TR from "@amcharts/amcharts5/locales/tr_TR";
import am5locales_en_US from "@amcharts/amcharts5/locales/en_US";
import NumberFormatter from '../../../formatters/NumberFormatter';
import DateFormatter from '../../../formatters/DateFormatter';
import moment from 'moment/moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ErrorWithReload from '../ErrorWithReload';

const ScoreLineChart = (props) => {// props: {data,averageValue,selectedSecurity,divId,onReload}
    const numberFormatter = new NumberFormatter;
    const dateFormatter = new DateFormatter();
    const [chartData, setChartData] = useState([]);
    const [theme, setTheme] = useState('dark'); // ['dark', 'light'
    const currentTheme = useContext(ThemeContext).theme;
    const [symbolCode, setSymbolCode] = useState('');
    const [symbolName, setSymbolName] = useState('');
    // Get the language code from redux store
    const { i18n } = useTranslation();
    const profileBasicDetail = useSelector(state => state.profile.basicDetail);
    const languageCode = profileBasicDetail.languagecode || i18n.language || 'tr-TR';
    const hasValidData = props.data && props.data.length > 0;

    useEffect(() => {
        if (props.data && props.data.length > 0) {
            // Ensure data is properly formatted
            const processedData = props.data.map(item => ({
                ...item,
                // No need to convert the date if it's already a timestamp
                date: typeof item.date === 'number' ? item.date : moment(String(item.date), "YYYYMMDD").valueOf()
            }));

            // Sort the data by date
            processedData.sort((a, b) => a.date - b.date);

            console.log("ScoreLineChart received data:", processedData);
            processedData.lastValue = processedData[processedData.length - 1]?.value;
            setChartData(processedData);
        }
    }, [props.data]); // Only depend on data changes

    useEffect(() => {
        // Separate effect for security details
        if (props.selectedSecurity) {
            if (props.selectedSecurity.code) {
                setSymbolCode(props.selectedSecurity.code);
            }
            if (props.selectedSecurity.name) {
                setSymbolName(props.selectedSecurity.name);
            }
        }
    }, [props.selectedSecurity?.code, props.selectedSecurity?.name]);

    useEffect(() => {
        // Separate effect for theme changes
        setTheme(currentTheme);
    }, [currentTheme]);


    useLayoutEffect(() => {
        if (!hasValidData || !props.divId) {
            return;
        }

        // Check if the element still exists in DOM (to prevent errors)
        const element = document.getElementById(props.divId);
        if (!element) {
            console.warn(`Element with ID ${props.divId} not found`);
            return;
        }

        // Check if a chart already exists for this div
        if (window.am5Charts && window.am5Charts[props.divId]) {
            console.log(`Disposing existing chart for ${props.divId}`);
            window.am5Charts[props.divId].dispose();
        }

        // Create new root and store reference in window for cleanup
        let root = am5.Root.new(props.divId);
        if (!window.am5Charts) window.am5Charts = {};
        window.am5Charts[props.divId] = root;

        // Skip animations in development for better performance
        const isDevEnvironment = process.env.NODE_ENV === 'development';
        if (!isDevEnvironment) {
            root.setThemes([am5themes_Animated.new(root)]);
        } else {
            console.log('Running in dev mode - animations disabled for performance');
        }

        // Dynamically set locale based on language code
        if (languageCode === 'en-US') {
            root.locale = am5locales_en_US;
            console.log("Using English locale for chart");
        } else {
            // Default to Turkish
            root.locale = am5locales_tr_TR;
            console.log("Using Turkish locale for chart");
        }

        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelX: "none",
                wheelY: "none",
                pinchZoomX: false,
                layout: root.verticalLayout,
                paddingTop: 15,
                paddingBottom: 10,
                paddingLeft: 30,
                paddingRight: 0,
                numberFormatter: am5.NumberFormatter.new(root, {
                    numericFormat: "#,###,###",
                    bigNumberPrefixes: [
                        { number: 1e3, suffix: "K" },
                        { number: 1e6, suffix: "M" },
                        { number: 1e9, suffix: "B" },
                        { number: 1e12, suffix: "T" },
                    ],
                }),
                cursor: am5xy.XYCursor.new(root, {
                    behavior: "none",
                }),

            })
        );


        const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
        cursor.lineY.set("visible", false);

        // Create Start X-Axis
        const axisRendererX = am5xy.AxisRendererX.new(root, {
            minGridDistance: 60
        });


        axisRendererX.labels.template.setAll({
            rotation: 30,
            centerX: am5.p50,
            centerY: am5.p50,
            paddingTop: 6,
            fontSize: 12,
            fontFamily: 'Roboto-Medium',
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
            oversizedBehavior: 'wrap'
        });

        axisRendererX.grid.template.setAll({ visible: false });

        const xAxis = chart.xAxes.push(
            am5xy.GaplessDateAxis.new(root, {
                baseInterval: { timeUnit: 'day', count: 1 },
                maxDeviation: 0.3,
                groupData: false,
                connect: true,
                renderer: axisRendererX,
                tooltip: am5.Tooltip.new(root, {
                    keepTargetHover: true,
                }),
            })
        );

        // const xAxis = chart.xAxes.push(
        //     am5xy.GaplessDateAxis.new(root, {
        //         maxDeviation: 0.3,
        //         groupData: false,

        //         // baseInterval: {
        //         //     timeUnit: "day",
        //         //     count: 1
        //         // },
        //         // renderer: am5xy.AxisRendererX.new(root, {
        //         //     minGridDistance: 50,
        //         //     minorGridEnabled: true,

        //         // }),
        //         // tooltip: am5.Tooltip.new(root, {
        //         //     keepTargetHover: true,
        //         // }),
        //     })
        // );





        xAxis.set('minorDateFormats', { day: 'dd', month: 'MM' });
        xAxis.set('numberFormat', '#,###.');
        xAxis.set("gridIntervals", [{ timeUnit: "month", count: 6 }]);

        // Create End X-Axis

        // Create Start Y-Axis
        var yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {
                pan: "zoom",
                opposite: true,
            })
        }));
        yAxis.get("renderer").grid.template.setAll({
            strokeOpacity: 1,
            stroke: theme === 'dark' ? am5.color('#626262') : am5.color('#E4E4E4'),
            strokeDasharray: [4, 4],
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
            opposite: true,
        });

        yAxis.get("renderer").labels.template.setAll({
            oversizedBehavior: "wrap",
            maxWidth: 250,
            textAlign: "center",
            fontSize: 12,
            fontFamily: 'Roboto-Medium',
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
            opacity: 1,
        });
        yAxis.set("numberFormat", "#,###.");

        // Create End Y-Axis
        var right = yAxis.get("renderer").get("opposite");
        var tooltipHTML = createTooltipHTML(theme);

        let tooltipLineSeries = am5.Tooltip.new(root, {
            keepTargetHover: true,
            getFillFromSprite: true,
            getStrokeFromSprite: true,
            autoTextColor: true,
            getLabelFillFromSprite: true,
            fill: am5.color("#333"),
        });

        tooltipLineSeries.get("background").setAll({
            fill: am5.color("#333"),
            fillOpacity: 0,
            strokeOpacity: 0,
            getStrokeFromSprite: true,
        });
        tooltipLineSeries.label.set("fill", am5.color("#231f20"));

        var series = chart.series.push(
            am5xy.LineSeries.new(root, {
                name: 'name',
                minBulletDistance: 10, // Reduced from 40 to show more data points
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "value",
                valueXField: "date",
                stroke: am5.color('#42A5F5'),
                connect: true,
                groupDataDisabled: true, // Disable grouping to show all data points
                tooltip: tooltipLineSeries,
                tooltipHTML: tooltipHTML,
            })
        );
        series.strokes.template.setAll({
        });
        series.get("tooltip").label.set("text", "[bold]{Date.formatDate('dd.MM.YYYY')}[/]\nFiyat: {valueY}");

        // // Make the bullets more visible
        // series.bullets.push(function () {
        //     return am5.Bullet.new(root, {
        //         sprite: am5.Circle.new(root, {
        //             radius: 4, // Increase radius
        //             fill: am5.color('#42A5F5'),
        //             stroke: am5.color('#ffffff'), // Add white border for visibility
        //             strokeWidth: 2
        //         })
        //     });
        // });

        console.log('chartData before setting to series:', chartData);

        if (chartData && chartData.length > 0) {
            // No need to modify dates again as we've already processed them in useEffect
            series.data.setAll(chartData);

            if (chartData.length > 0) {

                const lastVal = chartData[chartData.length - 1].value;

                const lastItem = yAxis.makeDataItem({ value: lastVal });
                const lastRange = yAxis.createAxisRange(lastItem);

                const lastLabel = lastRange.get("label");

                lastLabel.setAll({
                    text: root.numberFormatter.format(lastVal, "#,###.00"),
                    populateText: true,
                    fontFamily: "Roboto-Medium",
                    fontSize: 12,
                    fill: am5.color("#FFFFFF"),
                    background: am5.RoundedRectangle.new(root, {
                        fill: am5.color("#42A5F5"),
                        strokeWidth: 0,
                        cornerRadiusTL: 4,
                        cornerRadiusTR: 4,
                        cornerRadiusBL: 4,
                        cornerRadiusBR: 4
                    }),
                    paddingLeft: 6,
                    paddingRight: 6,
                    paddingTop: 3,
                    paddingBottom: 3
                });


                lastLabel.adapters.add("x", (x) => {
                    return yAxis.width() - lastLabel.get("pixelWidth") - 4;
                });
                lastLabel.set("centerY", am5.p50);
            }
        }

        var rangeDataItem = yAxis.makeDataItem({
            value: props.averageValue,
            paddingLeft: 0,
        });
        var range = yAxis.createAxisRange(rangeDataItem);
        var label = range.get("label");

        label.setAll({
            fill: theme === 'Dark' ? am5.color('#231F20') : am5.color('#231F20'),
            text: props.averageValue,
            dx: -22,
            background: am5.RoundedRectangle.new(root, {
                fill: theme === 'Dark' ? am5.color('#E0E0E0') : am5.color('#E0E0E0'),
            }),
            populateText: true,
        });

        label.adapters.add("x", function (x, target) {
            return 0;
        })

        chart.plotContainer.children.push(label);

        range.get("grid").setAll({
            visible: true,
            stroke: theme === 'dark' ? am5.color('#E0E0E0') : am5.color('#E4E4E4'),
            strokeDasharray: [0, 0],
            strokeWidth: 4,
            dx: 40,
        });

        series.appear(1000);
        chart.appear(1000, 100);

        return () => {
            root.dispose();
        };
    }, [props.divId, hasValidData, chartData, theme, languageCode]); // Added props.divId as first dependency, since it's critical

    const createTooltipHTML = () => {
        const dateLabel = languageCode === 'en-US' ? 'Date' : 'Tarih';
        const priceLabel = languageCode === 'en-US' ? 'Price' : 'Fiyat';
        const averageLabel = languageCode === 'en-US' ? 'Average' : 'Ortalama';

        const priceColor = '#2FBC99';
        const volumeColor = theme === 'dark' ? '#e0e0e0' : '#757575';
        const backgroundColor = theme === 'dark' ? 'rgba(18, 18, 18, 0.3)' : 'rgba(255, 255, 255, 0.3)';
        const textColor = theme === 'dark' ? '#fff' : '#231f20';

        return `<div style="padding: 5px; background-color: ${backgroundColor}; color: ${textColor}; border-radius: 5px; min-width: 140px; border: 0.5px solid #b5b5b5; backdrop-filter: blur(4px);">
    <div style="display:block; text-align:left; margin-bottom:0px;">
      <strong>${symbolCode} <span style="color: ${priceColor}"> - ${symbolName}</span></strong>
    </div>

    <div style="display:block; margin-bottom:0px;">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0px;">
        <div style="display:flex; align-items:center;">
          <span style="font-size:13px; font-family:Roboto-Medium; background-color: ${priceColor}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right:2px;"></span>
          <span style="font-size:13px; font-family:Roboto-Medium;">${dateLabel}:</span>
        </div>
        <div style="text-align:right;">
          <span style="font-size:13px; font-family:Roboto-Medium;">{date.formatDate('dd/MM/yyyy')}</span>
        </div>
      </div>

      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0px;">
        <div style="display:flex; align-items:center;">
          <span style="font-size:13px; font-family:Roboto-Medium; background-color: ${priceColor}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right:2px;"></span>
          <span style="font-size:13px; font-family:Roboto-Medium;">${priceLabel}:</span>
        </div>
        <div style="text-align:right;">
          <span style="font-size:13px; font-family:Roboto-Medium;">{valueY} ${props.currencySymbol || '₺'}</span>
        </div>
      </div>

      <div style="display:flex; justify-content:space-between; align-items:center;">
        <div style="display:flex; align-items:center;">
          <span style="font-size:13px; font-family:Roboto-Medium; background-color: ${volumeColor}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right:2px;"></span>
          <span style="font-size:13px; font-family:Roboto-Medium;">${averageLabel}:</span>
        </div>
        <div style="text-align:right;">
          <span style="font-size:13px; font-family:Roboto-Medium;">${props.averageValue || 0}</span>
        </div>
      </div>
    </div>
  </div>`;
    };



    // Check if we have valid data to render the chart

    return (
        <div className='index-analysis-line-chart'>
            {hasValidData ? (
                <div id={props.divId} style={{ width: "100%", height: "315px" }}></div>
            ) : (
                <ErrorWithReload
                    onReload={props.onReload}
                    height="315px"
                />
            )}
        </div>
    )
}

export default ScoreLineChart;
